import {
    Circle,
    Fill,
    Icon,
    RegularShape,
    Stroke,
    Style,
    Text,
} from "ol/style";
import iconList from "js/tripper/icons.js";

const IMPRESSIONS = iconList.impressionEmoji;
const EMOJI_FONT_STACK =
    '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
const NONE_KEY = "__none__";
const GLYPH_CANVAS_SIZE = 64;
const GLYPH_VISIBLE_TARGET = GLYPH_CANVAS_SIZE * 0.6;
const GLOW_CANVAS_SIZE = Math.round(GLYPH_CANVAS_SIZE * 1.6);

const glyphCanvasCache = {};
const glowCanvasCache = {};

function measureGlyphBounds(context, size) {
    const { data, width, height } = context.getImageData(0, 0, size, size);
    let minX = size;
    let minY = size;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (data[(y * width + x) * 4 + 3] > 10) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }
    }
    if (maxX < minX) {
        return { minX: 0, minY: 0, maxX: size, maxY: size };
    }

    return { minX, minY, maxX, maxY };
}

function renderGlyphCanvas(emoji) {
    const probeSize = GLYPH_CANVAS_SIZE * 2;
    const probeFontSize = probeSize * 0.7;
    const anchorX = probeSize * 0.15;
    const anchorY = probeSize * 0.75;

    const probe = document.createElement("canvas");
    probe.width = probeSize;
    probe.height = probeSize;
    const probeContext = probe.getContext("2d");
    probeContext.font = `${probeFontSize}px ${EMOJI_FONT_STACK}`;
    probeContext.textAlign = "left";
    probeContext.textBaseline = "alphabetic";
    probeContext.fillText(emoji, anchorX, anchorY);

    const bounds = measureGlyphBounds(probeContext, probeSize);
    const glyphWidth = bounds.maxX - bounds.minX;
    const glyphHeight = bounds.maxY - bounds.minY;
    const glyphCenterX = bounds.minX + glyphWidth / 2;
    const glyphCenterY = bounds.minY + glyphHeight / 2;
    const scale = GLYPH_VISIBLE_TARGET / Math.max(glyphWidth, glyphHeight, 1);

    const canvas = document.createElement("canvas");
    canvas.width = GLYPH_CANVAS_SIZE;
    canvas.height = GLYPH_CANVAS_SIZE;
    const context = canvas.getContext("2d");
    context.font = `${probeFontSize * scale}px ${EMOJI_FONT_STACK}`;
    context.textAlign = "left";
    context.textBaseline = "alphabetic";
    context.fillText(
        emoji,
        GLYPH_CANVAS_SIZE / 2 - (glyphCenterX - anchorX) * scale,
        GLYPH_CANVAS_SIZE / 2 - (glyphCenterY - anchorY) * scale
    );

    return canvas;
}

function renderNoImpressionCanvas() {
    const canvas = document.createElement("canvas");
    canvas.width = GLYPH_CANVAS_SIZE;
    canvas.height = GLYPH_CANVAS_SIZE;
    const context = canvas.getContext("2d");
    const center = GLYPH_CANVAS_SIZE / 2;
    context.beginPath();
    context.arc(center, center, GLYPH_VISIBLE_TARGET / 2, 0, Math.PI * 2);
    context.fillStyle = "rgba(255, 255, 255, 0.95)";
    context.fill();
    context.lineWidth = 3;
    context.strokeStyle = "rgba(0, 0, 0, 0.35)";
    context.stroke();

    return canvas;
}

function getGlyphCanvas(emojiKey) {
    if (glyphCanvasCache[emojiKey]) {
        return glyphCanvasCache[emojiKey];
    }

    const canvas =
        emojiKey === NONE_KEY
            ? renderNoImpressionCanvas()
            : renderGlyphCanvas(emojiKey);
    glyphCanvasCache[emojiKey] = canvas;

    return canvas;
}

function getGlowCanvas(emojiKey) {
    if (glowCanvasCache[emojiKey]) {
        return glowCanvasCache[emojiKey];
    }

    const base = getGlyphCanvas(emojiKey);
    const canvas = document.createElement("canvas");
    canvas.width = GLOW_CANVAS_SIZE;
    canvas.height = GLOW_CANVAS_SIZE;
    const context = canvas.getContext("2d");
    const offset = (GLOW_CANVAS_SIZE - GLYPH_CANVAS_SIZE) / 2;
    context.shadowColor = "rgba(255, 255, 255, 0.95)";
    context.shadowBlur = GLYPH_CANVAS_SIZE * 0.22;
    for (let pass = 0; pass < 3; pass++) {
        context.drawImage(base, offset, offset);
    }
    glowCanvasCache[emojiKey] = canvas;

    return canvas;
}

function dot(color, strokeWidth) {
    return new Style({
        image: new Circle({
            radius: 6,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: "#fff", width: strokeWidth }),
        }),
    });
}

function regionFill(color) {
    return new Style({
        stroke: new Stroke({ width: 0, color: "#fff" }),
        fill: new Fill({ color }),
    });
}

const DOT_STYLES = {
    standard: dot("#d1462f", 2),
    highlighted: dot("#d1462f", 1),
    empty: dot("#f0a99d", 1),
};

const REGION_STYLES = {
    standard: regionFill("rgba(0, 0, 0, 0.3)"),
    marked: regionFill("rgba(209, 70, 47, 0.2)"),
    highlighted: regionFill("rgba(209, 70, 47, 0.8)"),
};

const emojiStyleCache = {};
const choroplethCache = {};
const glyphStyleCache = {};

export const crossStyle = new Style({
    image: new RegularShape({
        fill: new Fill({ color: "#d1462f" }),
        stroke: new Stroke({ color: "#d1462f", width: 4 }),
        points: 4,
        radius: 10,
        radius2: 0,
        angle: 0,
    }),
});

export function regionStyle(name) {
    return REGION_STYLES[name];
}

export function choroplethStyle(fraction) {
    const key = fraction.toFixed(3);
    if (!choroplethCache[key]) {
        choroplethCache[key] = regionFill(
            `hsl(210, 50%, ${(1 - Number(key)) * (90 - 25) + 25}%)`
        );
    }

    return choroplethCache[key];
}

function emojiStyle(impression) {
    if (!emojiStyleCache[impression]) {
        emojiStyleCache[impression] = new Style({
            text: new Text({
                text: IMPRESSIONS[impression],
                font: "8px Calibri,sans-serif",
                stroke: new Stroke({ width: 0 }),
                offsetY: 0,
            }),
        });
    }

    return emojiStyleCache[impression];
}

export function dotStyle() {
    return DOT_STYLES.standard;
}

export function pointStyleFor(geo) {
    const value = geo.getMeta()?.impression ?? geo.getValue();
    if (typeof value === "number") {
        return emojiStyle(value);
    }
    if (typeof value === "boolean") {
        return DOT_STYLES[value ? "highlighted" : "empty"];
    }

    return DOT_STYLES.standard;
}

const MAX_BUCKETS = 8;
const MIN_BUCKETS = 3;
const FIT_TARGET = 0.95;

function naturalBreaks(distinct, weights, count) {
    const size = distinct.length;
    const prefix = { count: [0], sum: [0], squares: [0] };
    distinct.forEach((value, index) => {
        const weight = weights[index];
        prefix.count.push(prefix.count[index] + weight);
        prefix.sum.push(prefix.sum[index] + weight * value);
        prefix.squares.push(prefix.squares[index] + weight * value * value);
    });
    const cost = (from, to) => {
        const count = prefix.count[to + 1] - prefix.count[from];
        const sum = prefix.sum[to + 1] - prefix.sum[from];
        const squares = prefix.squares[to + 1] - prefix.squares[from];

        return squares - (sum * sum) / count;
    };

    const best = [Array.from({ length: size }, (_, to) => cost(0, to))];
    const starts = [Array(size).fill(0)];
    for (let level = 1; level < count; level++) {
        best[level] = Array(size).fill(Infinity);
        starts[level] = Array(size).fill(level);
        for (let to = level; to < size; to++) {
            for (let from = level; from <= to; from++) {
                const total = best[level - 1][from - 1] + cost(from, to);
                if (total < best[level][to]) {
                    best[level][to] = total;
                    starts[level][to] = from;
                }
            }
        }
    }

    const classes = [];
    let to = size - 1;
    for (let level = count - 1; level >= 0; level--) {
        const from = starts[level][to];
        classes.unshift({ min: distinct[from], max: distinct[to] });
        to = from - 1;
    }

    return { classes, variance: best[count - 1][size - 1] };
}

export function valueBuckets(values, bands = null) {
    if (bands) {
        return bands
            .map(([min, max]) => ({ min, max }))
            .filter((band) =>
                values.some((value) => value >= band.min && value <= band.max)
            );
    }

    const distinct = [...new Set(values)].sort((a, b) => a - b);
    if (distinct.length <= MAX_BUCKETS) {
        return distinct.map((value) => ({ min: value, max: value }));
    }

    const weights = distinct.map(
        (value) => values.filter((item) => item === value).length
    );
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const totalVariance = values.reduce(
        (sum, value) => sum + (value - mean) ** 2,
        0
    );

    let result;
    for (let count = MIN_BUCKETS; count <= MAX_BUCKETS; count++) {
        result = naturalBreaks(distinct, weights, count);
        if (1 - result.variance / totalVariance >= FIT_TARGET) {
            break;
        }
    }

    return result.classes;
}

export function bucketFraction(buckets, value) {
    if (buckets.length < 2) {
        return 1;
    }

    return (
        buckets.findIndex((bucket) => value <= bucket.max) /
        (buckets.length - 1)
    );
}

export function regionStyleFor(value, buckets) {
    if (typeof value === "boolean") {
        return regionStyle(value ? "highlighted" : "marked");
    }
    if (typeof value === "number" && buckets) {
        return choroplethStyle(bucketFraction(buckets, value));
    }

    return regionStyle("standard");
}

export function glyphStyle(meta = {}, state = "normal") {
    const cacheKey = `${meta.impression}|${meta.isStay}|${state}`;
    if (glyphStyleCache[cacheKey]) {
        return glyphStyleCache[cacheKey];
    }

    const emojiKey =
        typeof meta.impression === "number"
            ? IMPRESSIONS[meta.impression]
            : NONE_KEY;
    const canvas = meta.isStay
        ? getGlowCanvas(emojiKey)
        : getGlyphCanvas(emojiKey);
    const canvasScale = meta.isStay ? GLOW_CANVAS_SIZE / GLYPH_CANVAS_SIZE : 1;
    const baseSize = meta.isStay ? 26 : 16;
    const displaySize =
        (state === "highlighted" ? baseSize * 1.2 : baseSize) * canvasScale;
    const opacity = state === "dimmed" ? 0.45 : 1;

    const style = new Style({
        zIndex: state === "highlighted" ? 10 : meta.isStay ? 1 : 0,
        image: new Icon({
            img: canvas,
            width: displaySize,
            height: displaySize,
            opacity,
        }),
    });

    glyphStyleCache[cacheKey] = style;

    return style;
}
